import {
    REACT_APP_ENQUIRY_ENDPOINT,
    REACT_APP_WEB3FORMS_ACCESS_KEY,
} from "../config/emailEnv";

const WEB3FORMS_URL = "https://api.web3forms.com/submit";

/**
 * Prefer env from bundled config (see ../config/emailEnv.js), then window fallback
 * from public/index.html (interpolated in production; dev may still set window).
 */
function getWeb3FormsAccessKey() {
    let key = String(REACT_APP_WEB3FORMS_ACCESS_KEY || "").trim();
    if (key) return key;

    if (typeof window !== "undefined" && window.__REACT_APP_WEB3FORMS_ACCESS_KEY__) {
        const wk = String(window.__REACT_APP_WEB3FORMS_ACCESS_KEY__).trim();
        // Dev server may leave HTML placeholder unreplaced
        if (wk && !wk.startsWith("%REACT_APP_") && wk !== "%REACT_APP_WEB3FORMS_ACCESS_KEY%") {
            return wk;
        }
    }
    return "";
}

function getCustomEnquiryEndpoint() {
    return String(REACT_APP_ENQUIRY_ENDPOINT || "").trim();
}

function parseWeb3FormsError(data) {
    if (!data || typeof data !== "object") {
        return "Could not send your message. Please try again.";
    }
    if (data.body && typeof data.body.message === "string") {
        return data.body.message;
    }
    if (typeof data.message === "string") {
        return data.message;
    }
    return "Could not send your message. Please try again.";
}

function buildEmailBody(data) {
    const main = (data.message && String(data.message).trim()) || "";
    const lines = [];
    if (data.phone) lines.push(`Phone: ${data.phone}`);
    if (data.property) lines.push(`Property: ${data.property}`);
    if (data.propertyType) lines.push(`Interested in: ${data.propertyType}`);
    if (data.subject && data.subject !== data.message) lines.push(`Subject: ${data.subject}`);
    if (data.preferredTime) lines.push(`Preferred contact time: ${data.preferredTime}`);
    if (data.source) lines.push(`Form: ${data.source}`);
    if (data.submittedAt) lines.push(`Submitted: ${data.submittedAt}`);
    const meta = lines.join("\n");
    if (main && meta) return `${main}\n\n---\n${meta}`;
    return main || meta || "(No message)";
}

/**
 * Sends enquiry to Web3Forms (email) if REACT_APP_WEB3FORMS_ACCESS_KEY is set,
 * otherwise POSTs JSON to REACT_APP_ENQUIRY_ENDPOINT if set.
 */
export async function submitEnquiry(payload) {
    const accessKey = getWeb3FormsAccessKey();
    const customEndpoint = getCustomEnquiryEndpoint();

    if (accessKey) {
        const messageText = buildEmailBody(payload);
        const res = await fetch(WEB3FORMS_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({
                access_key: accessKey,
                subject:
                    payload.subject ||
                    `Landmarks Realty — ${payload.source || "Website enquiry"}`,
                name: payload.name || "",
                email: payload.email || "",
                phone: payload.phone || "",
                message: messageText || "—",
                from_name: "Landmarks Realty Website",
            }),
        });

        let data;
        try {
            data = await res.json();
        } catch {
            throw new Error(
                "Could not reach the email service. Check your connection and try again."
            );
        }

        const success = data && data.success === true;
        if (!res.ok || !success) {
            if (process.env.NODE_ENV === "development") {
                console.error("[submitEnquiry] Web3Forms response:", data);
            }
            throw new Error(parseWeb3FormsError(data));
        }
        return data;
    }

    if (customEndpoint) {
        const res = await fetch(customEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        if (!res.ok) {
            throw new Error(`Request failed (${res.status}). Please try again.`);
        }
        return;
    }

    throw new Error(
        "Email is not configured. Create a file named .env or .env.local in the same folder as package.json, add REACT_APP_WEB3FORMS_ACCESS_KEY=your_key from web3forms.com, then stop and restart npm start."
    );
}
